import InfiniteScroll from 'react-infinite-scroll-component';
import { useCallback, useContext, useEffect, useId, useState } from 'react';
// import ArchiveSwitch from '@/modules/common/components/ArchiveSwitch.tsx';
import CreateReportModal from '@/modules/common/components/modals/CreateReportModal.tsx';
import ReportTemplateModal from '@/modules/common/components/modals/ReportTemplateModal.tsx';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { DeleteModal } from '@/modules/admin-dashboard/components/modals/DeleteModal.tsx';
import {
  useDeleteReportTemplateMutation,
  useGetReportsTemplatesQuery,
  useUpdateReportTemplateMutation,
} from '@/modules/admin-dashboard/queries/reports-templates.ts';
import {
  Button,
  DisclosureListItem,
  EmptyListState,
  ModalProviderContext,
  PageLayout,
  SearchField,
  Spinner,
} from 'ui';
import { EditIcon, PinFilledBlueIcon, PinIcon, TrashGrayIcon } from 'ui/icons';
import { ReportTemplateResponse } from 'compliance-api';

const TemplateReports = () => {
  const { openModal } = useContext(ModalProviderContext);
  const businessId = useGetCurrentBusinessId();
  const [searchValue, setSearchValue] = useState('');
  // const [archive, setArchive] = useState(false);

  const containerId = useId();

  useEffect(() => {
    setSearchValue('');
  }, [businessId]);

  const { data, isFetching, fetchNextPage, isFetchingNextPage, hasNextPage } =
    useGetReportsTemplatesQuery({
      page: 1,
      page_size: 18,
      filter_by_name: searchValue,
    });

  const { mutateAsync: updateTemplate, isLoading: isTemplateUpdating } =
    useUpdateReportTemplateMutation();

  const isLoading = isFetching && !isFetchingNextPage;

  const templates = (data?.pages || []).flatMap(({ data }) => data);

  const handleOpenTemplateModal = useCallback(
    (updatingTemplate?: ReportTemplateResponse) => {
      openModal(ReportTemplateModal, {
        updatingTemplateId: updatingTemplate?.id,
      });
    },
    [openModal],
  );

  const handleOpenCreateReportWithTemplateModal = useCallback(
    (templateId: number) => {
      openModal(CreateReportModal, { templateId });
    },
    [openModal],
  );

  const handleToggleFavoriteTemplate = useCallback(
    (id: number, is_favorite: boolean) => {
      updateTemplate({ id, is_favorite });
    },
    [updateTemplate],
  );

  const handleDeleteTemplate = useCallback(
    (template: ReportTemplateResponse) => {
      openModal(DeleteModal, {
        title: `template`,
        id: template.id,
        name: template.name,
        mutation: useDeleteReportTemplateMutation,
      });
    },
    [openModal],
  );
  return (
    <PageLayout title="Template reports">
      <div className="h-full flex flex-col gap-4">
        <div className="flex flex-wrap-reverse w-full mt-5 md:mt-0 justify-end items-center gap-3">
          {/*<ArchiveSwitch active={archive} onChange={setArchive} />*/}
          <div className="flex-1 min-w-64">
            <SearchField
              placeholder="Search"
              value={searchValue}
              onChange={setSearchValue}
              allowClear
            />
          </div>
          <div className="min-w-fit">
            <Button onClick={() => handleOpenTemplateModal()} variant="primary">
              Add new template
            </Button>
          </div>
        </div>

        {isLoading && (
          <div className="w-full h-full flex items-center justify-center">
            <Spinner size="lg" />
          </div>
        )}
        <EmptyListState
          title="No report templates created yet"
          description="Report templates allow you to save your most frequently used settings for quick report creation."
          isLoading={isLoading}
          isSearchUsed={!!searchValue}
          itemsCount={templates.length}
          classes={{ container: '!h-auto flex-1' }}
        />
        {!isLoading && !!templates.length && (
          <div id={containerId} className="overflow-y-auto">
            <InfiniteScroll
              next={fetchNextPage}
              dataLength={templates.length}
              hasMore={!!hasNextPage}
              className="w-full flex flex-col space-y-2"
              style={{ overflow: 'unset' }}
              loader={
                <div className="flex justify-center items-center h-16 w-full">
                  <Spinner size="md" variant="primary" />
                </div>
              }
              scrollableTarget={containerId}
            >
              {templates.map(template => (
                <DisclosureListItem
                  key={template.id}
                  classes={{
                    leftContent: 'max-w-[calc(100%_-_150px)]',
                    leftMainString: 'w-full',
                    rightContentUp: 'shrink-0',
                  }}
                  leftMainString={
                    <div
                      className="hover:underline cursor-pointer truncate max-w-[100%]"
                      onClick={handleOpenCreateReportWithTemplateModal.bind(null, template.id)}
                    >
                      {template.name}
                    </div>
                  }
                  rightContent={
                    <div className="space-x-6">
                      <button
                        onClick={handleToggleFavoriteTemplate.bind(
                          null,
                          template.id,
                          !template.is_favorite,
                        )}
                        disabled={isTemplateUpdating}
                      >
                        <img
                          alt="favorite"
                          src={template.is_favorite ? PinFilledBlueIcon : PinIcon}
                        />
                      </button>
                      <button onClick={handleOpenTemplateModal.bind(null, template)}>
                        <img alt="edit" src={EditIcon} />
                      </button>
                      <button onClick={handleDeleteTemplate.bind(null, template)}>
                        <img alt="remove" src={TrashGrayIcon} />
                      </button>
                    </div>
                  }
                />
              ))}
            </InfiniteScroll>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default TemplateReports;
